"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import dynamic from "next/dynamic";
import WaveformAnimation from "./WaveformAnimation";
import HintCard from "./HintCard";
import PrimaryCtaButton from "@/components/ui/PrimaryCtaButton";
import WaitingTipCard from "@/components/shared/WaitingTipCard";
import { DotsLoader, RingLoader } from "@/components/ui/Loader";
import InterviewContextPicker from "@/components/interview/InterviewContextPicker";
import { usePracticeQuestion } from "@/hooks/usePracticeQuestion";
import {
  getGuestCareerStage,
  type CareerStageValue,
} from "@/lib/guestCareerStage";
import { buildFilterPayload } from "@/lib/practiceFilterParams";
import apiClient from "@/lib/api";
import {
  EMPTY_RECORDING_MESSAGE,
  reportEmptyRecording,
  reportRecorderError,
} from "@/lib/audio/recordingFailure";
import { event as gtagEvent } from "@/lib/gtag";
import { parseGuestProfile } from "@/lib/guest-profile";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/useToast";
import { usePracticeLimit } from "@/hooks/usePracticeLimit";
import { usePrepareTts } from "@/hooks/usePrepareTts";
import InterviewerSelector from "@/components/interview/InterviewerSelector";
import {
  DEFAULT_INTERVIEWER_KEY,
  isInterviewerSelectionEnabled,
  resolveInterviewer,
  type InterviewerKey,
} from "@/lib/interviewers";
import {
  readPracticeInterviewerKey,
  savePracticeInterviewerKey,
} from "@/lib/interviewerPreference";
import {
  listTargetPositions,
  type TargetPosition,
} from "@/lib/api/targetPositions";
import {
  listJobHuntingFiles,
  type JobHuntingFile,
} from "@/lib/api/jobHuntingFiles";
import PracticeLimitModal from "./PracticeLimitModal";
import RecordingTooShortToast from "./RecordingTooShortToast";
import {
  appendAudioMetrics,
  startRecordingLevelMeter,
  type RecordingLevelMeter,
  type RecordingLevelMetrics,
} from "@/lib/audio/recordingLevelMeter";

const InterviewAvatar = dynamic(
  () => import("@/components/interview/InterviewAvatar"),
  {
    ssr: false,
    loading: () => (
      <div className="w-full flex flex-col items-center gap-2">
        <div className="relative w-full max-w-[850px] aspect-[4/3] sm:aspect-video rounded-2xl overflow-hidden flex items-center justify-center bg-surface-container-low">
          <RingLoader
            size={40}
            className="text-primary"
            label="面接官 AI を読み込んでいます"
          />
        </div>
        <p className="text-sm font-medium text-gray-600">面接官 AI</p>
      </div>
    ),
  },
);

const MIN_RECORDING_SECONDS = 10;
const MAX_RECORDING_SECONDS = 180; // 3分
const IS_DEV =
  process.env.NODE_ENV === "development" ||
  process.env.NEXT_PUBLIC_ENABLE_DEV_FEATURES === "true";

type InputMode = "voice" | "text";
type RecordingState = "idle" | "recording" | "analyzing";

function getGuestProfile() {
  if (typeof window === "undefined") return null;
  return parseGuestProfile(sessionStorage.getItem("guestProfile"));
}

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  return "audio/webm";
}

export default function PracticeClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  // 共通質問（?questionId=）か大学が登録した質問（?universityQuestionId= / #1861）かを解決する。
  // 大学質問は API から取るため、ready になるまで本文は空で、描画は末尾で分ける。
  const {
    status: questionStatus,
    question,
    retry: retryQuestion,
  } = usePracticeQuestion(searchParams);
  // 計測に載せる共通質問の ID。大学質問のときは無い
  const questionId = question.presetId;
  const isUniversityQuestion = !!question.universityQuestionId;

  const [targetPositions, setTargetPositions] = useState<TargetPosition[]>([]);
  const [jobHuntingFiles, setJobHuntingFiles] = useState<JobHuntingFile[]>([]);
  const [targetPositionId, setTargetPositionId] = useState<string | null>(null);
  const [jobHuntingFileIds, setJobHuntingFileIds] = useState<string[]>([]);
  const [contextLoadError, setContextLoadError] = useState(false);
  const [careerStage, setCareerStage] = useState<CareerStageValue | null>(null);

  const readyJobHuntingFiles = useMemo(
    () => jobHuntingFiles.filter((f) => f.extracted_text_ready),
    [jobHuntingFiles],
  );

  // ログイン済みのときのみコンテキスト一覧を取得する。ゲストはフェッチしない。
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [tpRes, jhfRes] = await Promise.all([
          listTargetPositions({ per_page: 50 }),
          listJobHuntingFiles({ per_page: 50 }),
        ]);
        if (cancelled) return;
        setTargetPositions(tpRes.items);
        setJobHuntingFiles(jhfRes.items);
      } catch {
        if (!cancelled) setContextLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ヒントの区分出し分け用に区分を解決する。ログイン済はプロフィール区分、
  // 未ログインは一時保存値（sessionStorage）を用いる。取得失敗時は null（従来ヒント）。
  useEffect(() => {
    if (!user) {
      setCareerStage(getGuestCareerStage());
      return;
    }
    let cancelled = false;
    apiClient
      .get("/users/me")
      .then((res) => {
        if (!cancelled) {
          const stage = res.data?.profile?.career_stage;
          setCareerStage(
            stage === "new_grad" ||
              stage === "mid_career" ||
              stage === "high_school"
              ? stage
              : null,
          );
        }
      })
      .catch(() => {
        // 取得失敗時は既定（従来ヒント）にフォールバックする
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // 全区分共通の質問で区分向け別ヒントがある場合は差し替える。
  const displayHint =
    careerStage === "high_school" && question.hint_high_school
      ? question.hint_high_school
      : careerStage === "new_grad" && question.hint_new_grad
      ? question.hint_new_grad
      : question.hint;

  const [inputMode, setInputMode] = useState<InputMode>("voice");
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [avatarPhase, setAvatarPhase] = useState("ready");
  const [hasStarted, setHasStarted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [textInput, setTextInput] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showTooShortToast, setShowTooShortToast] = useState(false);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const { unlimited, showModal: limitReached } = usePracticeLimit();

  // 面接官の選択（#1863）。セッションを持たないためブラウザに記憶する。SSR と初回描画の不一致を
  // 避けるため、記憶した値はマウント後に読み込む。選択の UI は素材と声が揃った環境でだけ出す。
  const interviewerSelectionEnabled = isInterviewerSelectionEnabled();
  const [interviewerKey, setInterviewerKey] = useState<InterviewerKey>(DEFAULT_INTERVIEWER_KEY);
  const [interviewerLoaded, setInterviewerLoaded] = useState(false);
  const interviewer = resolveInterviewer(interviewerSelectionEnabled ? interviewerKey : DEFAULT_INTERVIEWER_KEY);

  const {
    audioSrc,
    customVideoSrc,
    ttsReady,
    ttsPrepared,
    ttsFailed,
    prepareTts,
    playTts,
    markTtsReady,
  } = usePrepareTts({ interviewerKey: interviewer.key });

  const handleSpeakingChange = useCallback(
    (speaking: boolean) => {
      setIsSpeaking(speaking);
      if (!speaking) markTtsReady();
    },
    [markTtsReady],
  );

  useEffect(() => {
    if (recordingState === "recording") setAvatarPhase("recording");
    else if (recordingState === "analyzing") setAvatarPhase("analyzing");
    else if (!hasStarted) setAvatarPhase("ready");
    else setAvatarPhase("idle");
  }, [recordingState, hasStarted]);

  // 記憶した面接官をマウント後に読み込む。読み込み完了までは読み上げの準備を始めない
  // （既定の面接官で準備した直後に作り直す無駄を避ける）。
  useEffect(() => {
    setInterviewerKey(readPracticeInterviewerKey());
    setInterviewerLoaded(true);
  }, []);

  // 質問が確定し、面接官も決まった時点で読み上げを用意する。開始前に面接官を切り替えたときも
  // 作り直す（準備済みの音声・映像は前の面接官のもの）。大学質問は共通質問の ID を持たないため、
  // フックが返す学生向けの生成経路を使う（動画・静的音声の探索は ID が無いので飛ばされる）。
  useEffect(() => {
    if (questionStatus !== "ready" || !interviewerLoaded) return;
    prepareTts(question.text, question.presetId, {
      autoPlay: false,
      generatePath: question.ttsGeneratePath,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionStatus, interviewerLoaded, interviewer.key]);

  const handleInterviewerChange = useCallback((key: InterviewerKey) => {
    setInterviewerKey(key);
    savePracticeInterviewerKey(key);
  }, []);

  const levelMeterRef = useRef<RecordingLevelMeter | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isTooShortRef = useRef(false);
  const elapsedRef = useRef(0);

  // 採点待ち画面の URL。採点は非同期で進むため、送信後はこの画面へ移して完了を待つ（#1831）。
  // 採点が失敗したときに質問へ戻せるよう、質問と絞り込みの条件を引き継ぐ。
  const buildPendingHref = useCallback(
    (submissionId: string) => {
      // 大学が登録した質問は共通質問の ID を持たないため、戻り先はその質問の ID で引き継ぐ（#1861）。
      const params = new URLSearchParams(
        question.universityQuestionId
          ? { universityQuestionId: question.universityQuestionId }
          : { questionId: String(question.presetId ?? "") },
      );
      const career = searchParams.get("career");
      const job = searchParams.get("job");
      if (career && career !== "all") params.set("career", career);
      if (job && job !== "all") params.set("job", job);
      return `/practice/feedback/pending/${submissionId}?${params.toString()}`;
    },
    [question.presetId, question.universityQuestionId, searchParams],
  );

  const handleSubmitError = useCallback((err: unknown) => {
    if (err && typeof err === "object" && "response" in err) {
      const axiosErr = err as {
        response?: {
          status?: number;
          data?: {
            remaining?: number;
            error?: string;
            reason?: string;
            submission_id?: string;
          };
        };
      };
      // 前の回答がまだ採点中なら、送信は受け付けられていない代わりに、その受付の識別子が
      // 返る。採点待ちの画面へ移し、二重の送信で練習回数を消費させない（#1831）。
      if (
        axiosErr.response?.status === 409 &&
        axiosErr.response.data?.reason === "scoring_in_progress" &&
        axiosErr.response.data.submission_id
      ) {
        router.push(buildPendingHref(axiosErr.response.data.submission_id));
        return;
      }
      if (axiosErr.response?.status === 429) {
        if (axiosErr.response.data?.remaining !== undefined) {
          setShowLimitModal(true);
        } else {
          setApiError(
            "リクエスト回数の上限に達しました。しばらく時間をおいてからお試しください。",
          );
        }
        setRecordingState("idle");
        return;
      }
      // 外部サービスの障害は 503 で返る。422 と同じくサーバーの文言をそのまま出す。
      // 汎用文言に落とすと「音声の解析がタイムアウトしました」のような原因の案内が
      // ユーザーに届かなくなる（#1305）
      const status = axiosErr.response?.status;
      if ((status === 422 || status === 503) && axiosErr.response?.data?.error) {
        setApiError(axiosErr.response.data.error);
        setRecordingState("idle");
        return;
      }
    }
    setApiError("フィードバックの生成に失敗しました。もう一度お試しください。");
    setRecordingState("idle");
  }, [router, buildPendingHref]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setActiveStream(null);
  }, []);

  // 計測を止めて結果を確定させる。
  const stopLevelMeter = useCallback(() => {
    const meter = levelMeterRef.current;
    if (!meter) return null;
    meter.stop();
    levelMeterRef.current = null;
    return meter.getMetrics();
  }, []);

  useEffect(() => {
    return () => {
      clearTimer();
      stopLevelMeter();
      cleanupStream();
    };
  }, [clearTimer, cleanupStream, stopLevelMeter]);

  // コンテキストピッカー由来のトーストを一定時間後に自動で閉じる
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(hideToast, 5000);
    return () => clearTimeout(timer);
  }, [toast, hideToast]);

  const submitAudio = useCallback(
    async (audioBlob: Blob, metrics: RecordingLevelMetrics | null) => {
      if (recordingState === "analyzing") return;
      setRecordingState("analyzing");
      setApiError(null);

      const guestProfile = getGuestProfile();

      const mimeType = audioBlob.type || "audio/webm";
      const ext = mimeType.includes("mp4") ? "mp4" : "webm";

      const formData = new FormData();
      formData.append("audio_file", audioBlob, `answer.${ext}`);
      appendAudioMetrics(formData, metrics);
      formData.append("question", question.text);
      // 出題元を示すパラメータは質問の解決側が持つ（共通質問: question_id + question_category /
      // 大学質問: university_question_id）。テキスト送信と同じ組を使う
      for (const [key, value] of Object.entries(question.answerParams)) {
        formData.append(key, value);
      }
      if (guestProfile?.jobType) {
        formData.append("job_type", guestProfile.jobType);
      }
      if (guestProfile?.experienceLevel) {
        formData.append("experience_level", guestProfile.experienceLevel);
      }
      if (guestProfile?.industry) {
        formData.append("industry", guestProfile.industry);
      }
      const filterPayload = buildFilterPayload(searchParams);
      if (!filterPayload.selected_career_stage && !user && careerStage) {
        filterPayload.selected_career_stage = careerStage;
      }
      for (const [k, v] of Object.entries(filterPayload)) {
        formData.append(k, v);
      }

      // ログインユーザーが選択したコンテキストのみ付与する。
      // 配列は Rails が Array(params[...]) で受理できるよう job_hunting_file_ids[] キーを繰り返す。
      if (user) {
        if (targetPositionId) {
          formData.append("target_position_id", targetPositionId);
        }
        jobHuntingFileIds.forEach((id) => {
          formData.append("job_hunting_file_ids[]", id);
        });
      }

      try {
        const res = await apiClient.post("/answers", formData);
        router.push(buildPendingHref(res.data.submission_id));
      } catch (err) {
        handleSubmitError(err);
      }
    },
    [
      question,
      recordingState,
      router,
      searchParams,
      handleSubmitError,
      buildPendingHref,
      user,
      targetPositionId,
      jobHuntingFileIds,
      careerStage,
    ],
  );

  const startRecording = async () => {
    setApiError(null);
    setMicError(null);
    setElapsedSeconds(0);
    elapsedRef.current = 0;
    isTooShortRef.current = false;
    chunksRef.current = [];

    try {
      const stream =
        streamRef.current ??
        (await navigator.mediaDevices.getUserMedia({ audio: true }));
      streamRef.current = stream;
      setActiveStream(stream);

      // 録音中の音量を計測する。計測できない環境でも録音は続行する。
      levelMeterRef.current = startRecordingLevelMeter(stream);

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      // 録音中の異常は発生環境を追えるように報告する（#1842）。
      mediaRecorder.onerror = (event) => reportRecorderError(event.error);

      mediaRecorder.onstop = () => {
        clearTimer();
        // ストリームを止める前に計測を確定させる。
        const metrics = stopLevelMeter();
        cleanupStream();

        if (isTooShortRef.current) {
          const elapsed = elapsedRef.current;
          isTooShortRef.current = false;
          elapsedRef.current = 0;
          setRecordingState("idle");
          setElapsedSeconds(0);
          setShowTooShortToast(true);
          gtagEvent("recording_too_short", {
            elapsed_seconds: elapsed,
            question_id: questionId,
            question_category: question.weightCategory,
          });
          return;
        }

        const blob = new Blob(chunksRef.current, { type: mimeType });

        // チャンクが 1 つも届かないと 0 バイトの Blob がそのまま送られ、サーバで 400 に
        // なっていた（Issue #1842）。クイック面接の whisperAudioRecorder.ts と同じ判定で
        // 通信せずに案内を出し、待機状態へ戻す。案内は次の録音開始時に消える。
        if (blob.size === 0) {
          reportEmptyRecording({
            elapsedSeconds: elapsedRef.current,
            mimeType,
          });
          setMicError(EMPTY_RECORDING_MESSAGE);
          setRecordingState("idle");
          setElapsedSeconds(0);
          elapsedRef.current = 0;
          return;
        }

        submitAudio(blob, metrics);
      };

      mediaRecorder.start(1000);
      setRecordingState("recording");
      gtagEvent("practice_start", {
        question_id: questionId,
        question_category: question.weightCategory,
        input_mode: "voice",
      });

      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => {
          const next = prev + 1;
          elapsedRef.current = next;
          if (next >= MAX_RECORDING_SECONDS) {
            mediaRecorderRef.current?.stop();
          }
          return next;
        });
      }, 1000);
    } catch {
      stopLevelMeter();
      setMicError(
        "マイクのアクセスが許可されていません。ブラウザの設定を確認してください。",
      );
    }
  };

  const stopRecording = () => {
    if (recordingState !== "recording") return;

    if (elapsedRef.current < MIN_RECORDING_SECONDS) {
      isTooShortRef.current = true;
    }

    mediaRecorderRef.current?.stop();
  };

  const submitText = async () => {
    if (!textInput.trim()) return;
    if (recordingState === "analyzing") return;
    setRecordingState("analyzing");
    setApiError(null);
    gtagEvent("practice_start", {
      question_id: questionId,
      question_category: question.weightCategory,
      input_mode: "text",
    });

    const guestProfile = getGuestProfile();

    try {
      const res = await apiClient.post("/answers", {
        question: question.text,
        transcript: textInput.trim(),
        ...question.answerParams,
        ...(guestProfile?.jobType && { job_type: guestProfile.jobType }),
        ...(guestProfile?.experienceLevel && {
          experience_level: guestProfile.experienceLevel,
        }),
        ...(guestProfile?.industry && {
          industry: guestProfile.industry,
        }),
        ...(() => {
          const payload = buildFilterPayload(searchParams);
          if (!payload.selected_career_stage && !user && careerStage) {
            payload.selected_career_stage = careerStage;
          }
          return payload;
        })(),
        // ログインユーザーが選択したコンテキストのみ付与する。
        ...(user && targetPositionId && { target_position_id: targetPositionId }),
        ...(user &&
          jobHuntingFileIds.length > 0 && {
            job_hunting_file_ids: jobHuntingFileIds,
          }),
      });
      router.push(buildPendingHref(res.data.submission_id));
    } catch (err) {
      handleSubmitError(err);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const dismissTooShortToast = useCallback(
    () => setShowTooShortToast(false),
    [],
  );

  const remainingSeconds = MAX_RECORDING_SECONDS - elapsedSeconds;

  const navigateToSelect = useCallback(() => {
    // 大学質問は質問選択画面ではなく大学連携から来る。戻り先もそこにする（#1861）
    if (isUniversityQuestion) {
      router.push("/university-link");
      return;
    }
    const params = new URLSearchParams();
    const career = searchParams.get("career");
    const job = searchParams.get("job");
    if (career && career !== "all") params.set("career", career);
    if (job && job !== "all") params.set("job", job);
    const qs = params.toString();
    router.push(qs ? `/practice/select?${qs}` : "/practice/select");
  }, [router, searchParams, isUniversityQuestion]);

  const cancelLink = (
    <button
      onClick={navigateToSelect}
      className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
    >
      キャンセル
    </button>
  );

  const inputModeToggle = IS_DEV ? (
    <div className="flex justify-center">
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setInputMode("voice")}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            inputMode === "voice"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          音声入力
        </button>
        <button
          onClick={() => setInputMode("text")}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            inputMode === "text"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          テキスト入力（開発用）
        </button>
      </div>
    </div>
  ) : null;

  // 読み上げを用意できなかったときの案内。無音・無エラーで潰さず、
  // 質問文を読んで練習を続けられることまで伝える（#1749）
  const ttsFailureNotice = ttsFailed ? (
    <p className="text-sm text-gray-500" role="status">
      音声を準備できませんでした。質問文を読んで回答してください。
    </p>
  ) : null;

  const statusMessage = (() => {
    if (recordingState !== "idle" || micError || apiError) return null;
    if (!hasStarted) {
      return (
        <div className="flex flex-col items-center gap-1 text-sm text-gray-500">
          <p>
            {ttsPrepared
              ? "準備ができたら、開始ボタンを押してください。"
              : "準備しています..."}
          </p>
          {ttsPrepared &&
            (ttsFailed ? (
              ttsFailureNotice
            ) : (
              <p>音声が再生されますのでご注意ください。</p>
            ))}
        </div>
      );
    }
    if (ttsFailed && !isSpeaking) return ttsFailureNotice;
    return (
      <p className="text-sm text-gray-500">
        {isSpeaking
          ? "質問を読み上げています..."
          : ttsReady
            ? "準備ができたら、ボタンを押して話してみてください"
            : "準備しています..."}
      </p>
    );
  })();

  const ctaButton = (() => {
    if (recordingState === "idle" && !hasStarted) {
      return (
        <PrimaryCtaButton
          onClick={() => {
            setHasStarted(true);
            playTts();
          }}
          disabled={!ttsPrepared || limitReached}
        >
          面接を開始する
        </PrimaryCtaButton>
      );
    }
    if (recordingState === "idle" && inputMode === "voice" && hasStarted) {
      return (
        <PrimaryCtaButton
          onClick={startRecording}
          disabled={isSpeaking || !ttsReady || limitReached}
        >
          <svg className="w-[18px] h-[18px] fill-white" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="7" />
          </svg>
          回答する
        </PrimaryCtaButton>
      );
    }
    if (
      recordingState === "idle" &&
      inputMode === "text" &&
      hasStarted &&
      IS_DEV
    ) {
      return (
        <PrimaryCtaButton
          onClick={submitText}
          disabled={!textInput.trim() || limitReached}
        >
          テキストで送信する
        </PrimaryCtaButton>
      );
    }
    if (recordingState === "recording") {
      return (
        <button
          onClick={stopRecording}
          className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-700 text-white font-semibold px-8 py-3.5 rounded-lg transition-all min-h-[48px]"
          style={{ opacity: elapsedSeconds < MIN_RECORDING_SECONDS ? 0.45 : 1 }}
        >
          <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
          回答を終了する
        </button>
      );
    }
    if (recordingState === "analyzing") {
      return (
        <button
          disabled
          className="inline-flex items-center justify-center gap-2 bg-border text-subtext font-semibold px-8 py-3.5 rounded-lg cursor-not-allowed min-h-[48px]"
        >
          考え中...
          <DotsLoader size={20} label="考え中" />
        </button>
      );
    }
    return null;
  })();

  // 大学質問の取得が済んでいない・失敗したときは練習の画面を出さない（#1861）。
  // 共通質問は同期で ready になるため、ここを通るのは大学質問だけである。
  if (questionStatus !== "ready") {
    const backLink = (
      <Link
        href="/university-link"
        className="text-sm font-bold text-primary underline"
      >
        大学連携へ戻る
      </Link>
    );
    return (
      <div
        data-testid="practice-question-state"
        className="flex-1 flex flex-col items-center justify-center gap-4 px-5 py-16 text-center"
      >
        {questionStatus === "loading" && (
          <RingLoader size={40} className="text-primary" label="質問を読み込んでいます" />
        )}
        {questionStatus === "unauthorized" && (
          <>
            <p className="font-bold text-on-surface">ログインが必要です</p>
            <p className="text-sm text-on-surface-variant">
              大学の質問で練習するには、大学から招待されたメールアドレスでログインしてください。
            </p>
            <Link
              href={`/login?next=${encodeURIComponent(`/practice?universityQuestionId=${question.universityQuestionId ?? searchParams.get("universityQuestionId") ?? ""}`)}`}
              className="min-h-11 inline-flex items-center rounded-lg bg-primary px-5 text-sm font-bold text-white"
            >
              ログインする
            </Link>
          </>
        )}
        {questionStatus === "not_found" && (
          <>
            <p className="font-bold text-on-surface">質問が見つかりません</p>
            <p className="text-sm text-on-surface-variant">
              削除された質問か、所属大学以外の質問の可能性があります。
            </p>
            {backLink}
          </>
        )}
        {questionStatus === "error" && (
          <>
            <p role="alert" className="text-sm text-error">
              質問を取得できませんでした。
            </p>
            <button
              type="button"
              onClick={retryQuestion}
              className="min-h-11 rounded-lg border border-primary px-5 text-sm font-bold text-primary"
            >
              再試行
            </button>
            {backLink}
          </>
        )}
      </div>
    );
  }

  return (
    // min-h-full（min-height:100%）は付けない。この要素はページ見出し・ガイド告知の
    // 後ろに並ぶ3番目の子であり、main の高さいっぱいを要求すると必ず先行分だけ
    // はみ出す。旧レイアウトでは main の高さが auto で百分率が効かず無害だったが、
    // main が flex-1 で確定高さを持つようになり、フッターの下に余白が出る形で表面化した。
    <div className="flex flex-col">
      {/* ヘッダー直下: 入力モード切替（DEV のみ） */}
      {inputModeToggle && (
        <div className="px-5 sm:px-8 pt-4 max-w-[1280px] w-full mx-auto">
          {inputModeToggle}
        </div>
      )}

      {/* メインエリア: PC は grid-cols-3、モバイルは縦積み */}
      <div
        data-testid="practice-pc-layout"
        className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-6 px-5 sm:px-8 pt-6 pb-10 max-w-[1280px] w-full mx-auto"
      >
        {/* メインコンテンツ（PC: col-span-2 / モバイル: 全幅） */}
        <div className="sm:col-span-2 flex flex-col items-center gap-7">
          <InterviewAvatar
            phase={avatarPhase}
            isSpeaking={isSpeaking}
            audioSrc={audioSrc}
            customVideoSrc={customVideoSrc}
            videoDir={interviewer.videoDir}
            onSpeakingChange={handleSpeakingChange}
          />

          {/* 面接官の選択（#1863）。アバターの直下に置き、面接を開始したら非表示にする */}
          {interviewerSelectionEnabled && !hasStarted && recordingState === "idle" && (
            <div className="w-full">
              <p className="text-sm font-bold mb-3">面接官</p>
              <InterviewerSelector value={interviewerKey} onChange={handleInterviewerChange} />
            </div>
          )}

          {/* アクションカード: 質問ピル + 質問テキスト + ステータス + CTA + キャンセル */}
          <div
            data-testid="practice-action-card"
            className="w-full bg-white rounded-2xl px-6 sm:px-10 py-7 sm:py-8 flex flex-col items-center gap-4 shadow-[0_2px_12px_rgba(44,26,30,0.04)]"
          >
            <span className="inline-block text-xs font-semibold text-accent border border-accent rounded-full px-3 py-0.5">
              質問
            </span>
            <h2 className="text-lg sm:text-xl font-bold text-center leading-relaxed">
              {question.text}
            </h2>

            {statusMessage && <div className="mt-1">{statusMessage}</div>}

            {recordingState === "analyzing" && (
              <div className="flex w-full flex-col items-center">
                <WaitingTipCard
                  statusMessage="考え中です..."
                  className="max-w-md"
                />
              </div>
            )}

            {IS_DEV &&
              inputMode === "text" &&
              recordingState === "idle" &&
              hasStarted && (
                <div className="w-full">
                  <textarea
                    className="w-full bg-white border border-gray-200 rounded-lg p-4 text-sm leading-relaxed resize-none outline-none focus:border-primary transition-colors min-h-[120px]"
                    placeholder="ここに回答を入力してください..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                  />
                </div>
              )}

            {recordingState === "recording" && (
              <div className="flex flex-col items-center gap-2">
                <WaveformAnimation stream={activeStream} />
                <p className="text-sm font-semibold text-subtext tabular-nums">
                  {formatTime(elapsedSeconds)} / 3:00
                </p>
                {remainingSeconds <= 30 && (
                  <p className="text-xs text-error">
                    残り {remainingSeconds} 秒
                  </p>
                )}
                {elapsedSeconds < MIN_RECORDING_SECONDS && (
                  <div className="flex items-center gap-2 text-xs text-subtext">
                    <div className="w-20 h-1 bg-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-1000 linear"
                        style={{
                          width: `${(elapsedSeconds / MIN_RECORDING_SECONDS) * 100}%`,
                        }}
                      />
                    </div>
                    <span>あと{MIN_RECORDING_SECONDS - elapsedSeconds}秒</span>
                  </div>
                )}
              </div>
            )}

            {apiError && <p className="text-sm text-error">{apiError}</p>}
            {micError && <p className="text-sm text-error">{micError}</p>}

            <div className="sr-only" aria-live="polite">
              {recordingState === "recording" && "回答中です"}
              {recordingState === "analyzing" && "考え中です"}
            </div>

            <div className="mt-2 flex flex-col items-center gap-3">
              {ctaButton}
              {cancelLink}
            </div>
          </div>
        </div>

        {/* ヒントカラム（PC: col-span-1 / モバイル: 全幅） */}
        <div className="sm:col-span-1 flex flex-col gap-6">
          <HintCard hint={displayHint} />
          {user && (
            <InterviewContextPicker
              targetPositions={targetPositions}
              jobHuntingFiles={readyJobHuntingFiles}
              selectedTargetPositionId={targetPositionId}
              selectedJobHuntingFileIds={jobHuntingFileIds}
              onChangeTargetPosition={setTargetPositionId}
              onChangeJobHuntingFileIds={setJobHuntingFileIds}
              onShowToast={(msg) => showToast(msg, "error")}
              loadError={contextLoadError}
            />
          )}
        </div>
      </div>

      {showTooShortToast && (
        <RecordingTooShortToast
          isGuest={!unlimited}
          onDismiss={dismissTooShortToast}
        />
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 animate-slide-up z-50 w-[calc(100%-2rem)] max-w-[420px]"
          style={{ transform: "translateX(-50%)" }}
          role="alert"
        >
          <div className="bg-white rounded-lg shadow-lg border border-border px-5 py-4 border-l-4 border-l-error">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-text">{toast.message}</p>
              <button
                type="button"
                onClick={hideToast}
                aria-label="閉じる"
                className="text-subtext hover:opacity-70 transition-opacity"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {(limitReached || showLimitModal) && <PracticeLimitModal />}
    </div>
  );
}
